import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Select,
  Radio as MuiRadio,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError } from "../../../utils/subscriptionError";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MuiTelInput } from "mui-tel-input";

// Redux actions
import {
  updateFieldsFlagAndMsg,
  updateFormData,
  clearAllButtons,
  setButtonsMode,
  // Normal Buttons
  addQuickReply,
  removeQuickReply,
  updateQuickReply,
  addPhoneButton,
  removePhoneButton,
  updatePhoneButton,
  addLinkButton,
  removeLinkButton,
  updateLinkButton,
  // Carousel
  updateCarouselSettings,
  addCarouselCard,
  removeCarouselCard,
  updateCarouselCardField,
  setCarouselCardBodyVariables,
  removeButtonFromCarouselCard,
  updateCarouselCardButtonField,
  uploadTemplateMedia,
} from "../../../redux/Slice/Templates/templateCreationSlice";

/** Max constraints for normal Buttons */
const MAX_TOTAL_BUTTONS = 10;
const MAX_QUICK_REPLIES = 10;
const MAX_PHONE_BUTTONS = 1;
const MAX_LINK_BUTTONS = 2;

/** Max constraints for Carousel */
const MAX_TOTAL_CARDS = 10; // up to 10 cards

/** Default country for the phone button's phone input, matching the onboarding page */
const DEFAULT_PHONE_COUNTRY = "US";

// Updated URL validation regex

// Helper functions for URL handling
const isDynamicUrl = (url) => {
  return url && url.endsWith("/{{1}}");
};

const extractBaseUrl = (url) => {
  if (!url) return "";
  return url.replace(/^https:\/\//, "").replace(/\/\{\{1\}\}$/, "");
};

const formatFullUrl = (baseUrl, isDynamic) => {
  const cleanBase = baseUrl
    .replace(/^https:\/\//, "")
    .replace(/\/\{\{1\}\}$/, "");
  return isDynamic ? `https://${cleanBase}/{{1}}` : `https://${cleanBase}`;
};

// Validate URL based on type

// =============================================
// ========== MEMOIZED SUB-COMPONENTS ==========
// =============================================

/** Memoized Quick Reply Item */
const QuickReplyItem = memo(({ reply, index, onChange, onRemove, error }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Quick Reply #{index + 1}
        </Typography>
        <IconButton onClick={() => onRemove(index)} sx={{ fontSize: "1.3rem" }}>
          <DeleteIcon fontSize="large" />
        </IconButton>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          Label
        </Typography>
        <TextField
          fullWidth
          placeholder="Reply option"
          value={reply.label}
          onChange={(e) => onChange(index, e.target.value)}
          error={!!error}
          helperText={error || `${reply.label.length}/20`}
          inputProps={{ maxLength: 20 }}
        />
      </Box>
    </Box>
  );
});

/** Memoized Link Button Item */
const LinkButtonItem = ({ linkBtn, index, onChange, onRemove, errors }) => {
  const dispatch = useDispatch();
  const { label, link } = linkBtn;

  // Determine if it's a dynamic URL based on the link format
  const isDynamic = isDynamicUrl(link);

  // Extract the editable part of the URL (domain and path)
  const baseUrl = extractBaseUrl(link);

  // Get the dynamic label from Redux
  const dynamicLabel = linkBtn.dynamicVariableLabel || "";

  const handleLabelChange = (value) => {
    const trimmed = value.trim();
    let errorMsg = "";

    // Validate button label - Set fieldsFlag to true only when the field is NOT empty
    if (!trimmed) {
      errorMsg = "Button label cannot be empty";
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: errorMsg,
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({ fieldsFlag: true, fieldsErrorMsg: "" })
      );
    }

    onChange(index, "label", value);
  };

  const handleIsDynamicChange = (isDynamic) => {
    // Format the URL based on whether it's dynamic or not
    const newLink = formatFullUrl(baseUrl, isDynamic);

    // Update the link in Redux
    onChange(index, "link", newLink);

    // Set the link type in Redux - explicitly set to true or false
    onChange(index, "isLinkDynamic", isDynamic);

    // If switching from dynamic to static, clear the dynamic variable label
    if (!isDynamic && dynamicLabel) {
      onChange(index, "dynamicVariableLabel", "");
    }

    // For dynamic URLs, validate the dynamic label
    if (isDynamic && !dynamicLabel.trim()) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: "Dynamic variable label is required",
        })
      );
    }
  };

  const handleBaseUrlChange = (value) => {
    // Format the complete URL including fixed parts
    const newLink = formatFullUrl(value, isDynamic);

    // Basic URL validation
    const errorMsg = !value.trim() ? "URL cannot be empty" : "";

    if (errorMsg) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: errorMsg,
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({ fieldsFlag: true, fieldsErrorMsg: "" })
      );
    }

    onChange(index, "link", newLink);
  };

  const handleDynamicLabelChange = (value) => {
    const trimmed = value.trim();

    // For dynamic URLs, label is mandatory
    if (isDynamic && !trimmed) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: "Dynamic variable label is required",
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({ fieldsFlag: true, fieldsErrorMsg: "" })
      );
    }

    onChange(index, "dynamicVariableLabel", value);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header with aligned delete icon */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Link Button #{index + 1}
        </Typography>
        <IconButton onClick={() => onRemove(index)} sx={{ fontSize: "1.3rem" }}>
          <DeleteIcon fontSize="large" />
        </IconButton>
      </Box>

      {/* Label field */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          Label
        </Typography>
        <TextField
          fullWidth
          value={label}
          error={!!errors.label}
          helperText={errors.label}
          onChange={(e) => handleLabelChange(e.target.value)}
          variant="outlined"
          placeholder="Button text here"
        />
      </Box>

      {/* URL Type Dropdown */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          URL Type
        </Typography>
        <Select
          fullWidth
          value={isDynamic ? "dynamic" : "static"}
          onChange={(e) => handleIsDynamicChange(e.target.value === "dynamic")}
        >
          <MenuItem value="static">Static</MenuItem>
          <MenuItem value="dynamic">Dynamic</MenuItem>
        </Select>
      </Box>

      {/* URL input with fixed parts */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          Link
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            border: (t) =>
              errors.link
                ? `1px solid ${t.palette.error.main}`
                : `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: (t) =>
              t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.common.white,
            "&:focus-within": {
              borderColor: "primary.main",
              borderWidth: 2,
            },
          }}
        >
          {/* Fixed https:// prefix */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[800] : "#f5f5f5",
              p: "8px 12px",
              borderRight: (t) => `1px solid ${t.palette.divider}`,
              color: (t) =>
                t.palette.mode === "dark"
                  ? t.palette.grey[200]
                  : t.palette.text.secondary,
              fontFamily: "monospace",
              fontSize: "0.875rem",
              flexShrink: 0,
            }}
          >
            https://
          </Box>

          {/* Editable domain/path part */}
          <Box
            component="input"
            value={baseUrl}
            onChange={(e) => handleBaseUrlChange(e.target.value)}
            placeholder="yourdomain.com/path"
            sx={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              py: "8px",
              px: "12px",
              fontFamily: "inherit",
              fontSize: "1rem",
              bgcolor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.common.white,
              color: "text.primary",
              "&::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            }}
          />

          {/* Fixed /{{1}} suffix for dynamic URLs */}
          {isDynamic && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: (t) =>
                  t.palette.mode === "dark" ? t.palette.grey[800] : "#f5f5f5",
                p: "8px 12px",
                borderLeft: (t) => `1px solid ${t.palette.divider}`,
                color: (t) =>
                  t.palette.mode === "dark"
                    ? t.palette.grey[200]
                    : t.palette.text.secondary,
                fontFamily: "monospace",
                fontSize: "0.875rem",
                flexShrink: 0,
              }}
            >
              {"/{{1}}"}
            </Box>
          )}
        </Box>
        {errors.link && (
          <Typography variant="caption" color="error">
            {errors.link}
          </Typography>
        )}
      </Box>

      {/* Dynamic variable label (only shown for dynamic URLs) */}
      {isDynamic && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            {"Label for {{1}}"}
          </Typography>
          <TextField
            fullWidth
            value={dynamicLabel}
            onChange={(e) => handleDynamicLabelChange(e.target.value)}
            error={isDynamic && !dynamicLabel.trim()}
            helperText={
              isDynamic && !dynamicLabel.trim() ? "This field is required" : ""
            }
            placeholder="e.g., User ID"
          />
        </Box>
      )}
    </Box>
  );
};

/** Memoized Carousel Card Button */
const CarouselCardButton = memo(({ btn, btnIndex, onChange, onRemove }) => {
  // Same picker as the non-carousel Phone Button (NormalButtonsSection) —
  // splits country code and national number for you, so a value already
  // containing the country code can't be re-entered into the number half
  // and double up (e.g. "+92" + "923245942955" -> an invalid number).
  // Seeded from the persisted countryCode/phoneNumber so the field doesn't
  // look cleared after a remount (e.g. navigating Back and returning here).
  const [phoneInputValue, setPhoneInputValue] = useState(() =>
    btn.countryCode || btn.phoneNumber
      ? `${btn.countryCode || ""}${btn.phoneNumber || ""}`
      : ""
  );

  const handlePhoneNumberChange = (value, info) => {
    setPhoneInputValue(value);
    const digitsOnly = (info.nationalNumber || "").replace(/\D/g, "");
    onChange(
      btnIndex,
      "countryCode",
      info.countryCallingCode ? `+${info.countryCallingCode}` : ""
    );
    onChange(btnIndex, "phoneNumber", digitsOnly);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
      <Typography variant="body2">
        Button {btnIndex + 1} ({btn.type})
      </Typography>
      <TextField
        label="Label"
        value={btn.label || ""}
        onChange={(e) => onChange(btnIndex, "label", e.target.value)}
      />
      {btn.type === "link" && (
        <TextField
          label="Link"
          value={btn.link || ""}
          onChange={(e) => onChange(btnIndex, "link", e.target.value)}
        />
      )}
      {btn.type === "phone" && (
        <MuiTelInput
          value={phoneInputValue}
          onChange={handlePhoneNumberChange}
          defaultCountry={DEFAULT_PHONE_COUNTRY}
          label="Phone Number"
          helperText="Pick your country flag, then enter your number"
        />
      )}
      <IconButton onClick={() => onRemove(btnIndex)}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
});

/** Memoized Marketing Carousel Section */
const MarketingCarouselSection = memo(() => {
  const dispatch = useDispatch();
  const {
    carouselCards,
    carouselMediaType,
    carouselNumButtons,
    carouselBtnOneType,
    carouselBtnTwoType,
  } = useSelector((state) => state.templateCreation);

  // State to hold all card previews, independent from Redux data
  const [localPreviews, setLocalPreviews] = useState([]);

  // Initialize localPreviews array if cards change
  useEffect(() => {
    // Ensure localPreviews array is same length as carouselCards
    if (localPreviews.length !== carouselCards.length) {
      setLocalPreviews((prev) => {
        const newPreviews = [...prev];
        // Add nulls for any new cards that don't have previews yet
        while (newPreviews.length < carouselCards.length) {
          newPreviews.push(null);
        }
        // Remove extras if cards were deleted
        return newPreviews.slice(0, carouselCards.length);
      });
    }
  }, [carouselCards.length, localPreviews.length]);

  const handleAddCard = () => {
    if (carouselCards.length >= MAX_TOTAL_CARDS) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Max of ${MAX_TOTAL_CARDS} carousel cards reached.`,
        })
      );
      return;
    }
    dispatch(addCarouselCard());
    // Add a null placeholder for the new card's preview
    setLocalPreviews((prev) => [...prev, null]);
  };

  // Handle updating a specific card's preview
  const updateCardPreview = (index, previewUrl) => {
    setLocalPreviews((prev) => {
      const updated = [...prev];
      updated[index] = previewUrl;
      return updated;
    });
  };

  // Handle card removal
  const handleRemoveCard = (index) => {
    dispatch(removeCarouselCard(index));
    // Also remove the preview for this card
    setLocalPreviews((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold">
        Carousel Configuration
      </Typography>

      {/* 1) Global media type */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography>Carousel media type</Typography>
        <Select
          value={carouselMediaType}
          onChange={(e) =>
            dispatch(
              updateCarouselSettings({
                field: "carouselMediaType",
                value: e.target.value,
              })
            )
          }
        >
          <MenuItem value="image">Image</MenuItem>
          <MenuItem value="video">Video</MenuItem>
        </Select>
      </Box>

      {/* 2) # of buttons (1 or 2) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography>No. of buttons</Typography>
        <Select
          value={carouselNumButtons}
          onChange={(e) => {
            dispatch(
              updateCarouselSettings({
                field: "carouselNumButtons",
                value: parseInt(e.target.value, 10),
              })
            );
          }}
        >
          <MenuItem value={1}>1</MenuItem>
          <MenuItem value={2}>2</MenuItem>
        </Select>
      </Box>

      {/* 3) Button #1 Type */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography>Button one type</Typography>
        <Select
          value={carouselBtnOneType}
          onChange={(e) =>
            dispatch(
              updateCarouselSettings({
                field: "carouselBtnOneType",
                value: e.target.value,
              })
            )
          }
        >
          <MenuItem value="quickReply">Quick Reply</MenuItem>
          <MenuItem value="link">Link</MenuItem>
          <MenuItem value="phone">Phone</MenuItem>
        </Select>
      </Box>

      {/* 4) Button #2 Type (only if # of buttons = 2) */}
      {carouselNumButtons === 2 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography>Button two type</Typography>
          <Select
            value={carouselBtnTwoType}
            onChange={(e) =>
              dispatch(
                updateCarouselSettings({
                  field: "carouselBtnTwoType",
                  value: e.target.value,
                })
              )
            }
          >
            <MenuItem value="quickReply">Quick Reply</MenuItem>
            <MenuItem value="link">Link</MenuItem>
            <MenuItem value="phone">Phone</MenuItem>
          </Select>
        </Box>
      )}

      {/* 5) Add Card */}
      <Box>
        <Button variant="outlined" onClick={handleAddCard}>
          Add card
        </Button>
        <Typography variant="caption" sx={{ ml: 2 }}>
          Media is required for each card
        </Typography>
      </Box>

      {/* 6) Render each card */}
      {carouselCards.map((card, cardIndex) => (
        <CarouselCard
          key={`card-${cardIndex}`}
          cardIndex={cardIndex}
          card={card}
          onRemoveCard={() => handleRemoveCard(cardIndex)}
          globalMediaType={carouselMediaType}
          localPreview={localPreviews[cardIndex]}
          updatePreview={(previewUrl) =>
            updateCardPreview(cardIndex, previewUrl)
          }
        />
      ))}
    </Box>
  );
});

/** Memoized Carousel Card Component */
const CarouselCard = memo(
  ({
    cardIndex,
    card,
    onRemoveCard,
    globalMediaType,
    localPreview,
    updatePreview,
  }) => {
    const dispatch = useDispatch();
    const { header, body, bodyVariables, buttons } = card;
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    // ============== 1) Variable detection for body ==============
    useEffect(() => {
      const regex = /{{(\d+)}}/g;
      const foundIds = [...body.matchAll(regex)].map((m) => parseInt(m[1], 10));

      // Build a new array of var objects
      const newBodyVars = foundIds.map((id) => {
        const existing = bodyVariables.find((v) => v.id === id);
        return existing || { id, value: "" };
      });

      // If there's a difference, dispatch => setCarouselCardBodyVariables
      if (JSON.stringify(newBodyVars) !== JSON.stringify(bodyVariables)) {
        dispatch(
          setCarouselCardBodyVariables({
            index: cardIndex,
            bodyVariables: newBodyVars,
          })
        );
      }
    }, [body, bodyVariables, cardIndex, dispatch]);

    // ============== 2) Handle body text changes ==============
    const handleBodyChange = (e) => {
      dispatch(
        updateCarouselCardField({
          index: cardIndex,
          field: "body",
          value: e.target.value,
        })
      );
    };

    // ============== 3) Update each bodyVariable's value ==============
    const handleBodyVarChange = (varId, newVal) => {
      const updatedVars = bodyVariables.map((v) =>
        v.id === varId ? { ...v, value: newVal } : v
      );
      dispatch(
        setCarouselCardBodyVariables({
          index: cardIndex,
          bodyVariables: updatedVars,
        })
      );
    };

    // ============== 4) File Upload => store in headerData ==============
    const handleFileUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const ext = file.name.split(".").pop().toLowerCase();

      if (header === "image" && !["jpg", "jpeg", "png"].includes(ext)) {
        toast.error("Please upload a valid image file (jpg, jpeg, png)");
        return;
      }
      if (header === "video" && !["mp4", "mov"].includes(ext)) {
        toast.error("Please upload a valid video file (mp4, mov)");
        return;
      }

      // Create local preview URL for immediate display
      const fileURL = URL.createObjectURL(file);
      // Update the local preview in parent component
      updatePreview(fileURL);

      // Start uploading to server
      setIsUploading(true);
      setUploadSuccess(false);

      try {
        // Create form data for upload
        const formData = new FormData();
        formData.append("file", file);

        // Upload file using the uploadTemplateMedia action
        const resultAction = await dispatch(
          uploadTemplateMedia(formData)
        ).unwrap();

        if (resultAction && resultAction.header) {
          // Store the server response URL in the Redux store
          dispatch(
            updateCarouselCardField({
              index: cardIndex,
              field: "headerData",
              value: resultAction.header,
            })
          );
          setUploadSuccess(true);
          toast.success("File uploaded successfully");
        } else {
          toast.error("Failed to get upload URL");
        }
      } catch (error) {
        console.error("Upload error:", error);
        const errorMessage = error?.userFriendlyMessage ||
          error?.message ||
          error?.details ||
          "Failed to upload file";
        if (!isSubscriptionBlockedError(error)) {
          toast.error(errorMessage);
        }
      } finally {
        setIsUploading(false);
      }
    };

    // ============== 5) Button fields ==============
    const handleButtonChange = (btnIndex, field, value) => {
      dispatch(
        updateCarouselCardButtonField({
          cardIndex,
          btnIndex,
          field,
          value,
        })
      );
    };

    const handleRemoveButton = (btnIndex) => {
      dispatch(removeButtonFromCarouselCard({ cardIndex, btnIndex }));
    };

    return (
      <Box sx={{ border: "1px solid #ccc", p: 2, mt: 2, borderRadius: 2 }}>
        <Typography fontWeight="bold">Card {cardIndex + 1}</Typography>

        {/* ============= Header Upload ============= */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
          {header !== "none" && (
            <Button
              variant="contained"
              component="label"
              disabled={isUploading}
              sx={{
                position: 'relative',
                minWidth: '140px',
                ...(isUploading && {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                })
              }}
            >
              {isUploading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  Uploading...
                </Box>
              ) : (
                `Upload ${header === "image" ? "Image" : "Video"}`
              )}
              <input
                type="file"
                hidden
                accept={header === "image" ? ".jpg,.jpeg,.png" : ".mp4,.mov"}
                onChange={handleFileUpload}
                disabled={isUploading}
              />
            </Button>
          )}
          {(uploadSuccess || (card.headerData && !localPreview)) && !isUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="body2" color="success.main">
                {uploadSuccess
                  ? "Uploaded Successfully"
                  : "Media uploaded — re-upload to change preview"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Preview the uploaded file - always use localPreview for display.
            If it's missing (e.g. after navigating Back and returning here) but
            the card already has an uploaded file handle, the checkmark above
            already confirms the upload wasn't lost, so no thumbnail is shown. */}
        {header === "image" && localPreview && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Preview:
            </Typography>
            <img src={localPreview} alt="Preview" style={{ maxWidth: 200 }} />
          </Box>
        )}
        {header === "video" && localPreview && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Preview:
            </Typography>
            <video src={localPreview} controls width="240" />
          </Box>
        )}

        {/* ============= Body + Placeholders ============= */}
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Body"
            multiline
            rows={3}
            fullWidth
            value={body}
            onChange={handleBodyChange}
            helperText="Use {{1}}, {{2}} for placeholders"
          />
        </Box>
        {bodyVariables.map((bv) => (
          <Box
            key={bv.id}
            sx={{ display: "flex", alignItems: "center", mt: 1 }}
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              Value for {`{${bv.id}}}`}:
            </Typography>
            <TextField
              size="small"
              value={bv.value}
              onChange={(e) => handleBodyVarChange(bv.id, e.target.value)}
            />
          </Box>
        ))}

        {/* ============= Buttons ============= */}
        {buttons.map((btn, i) => (
          <CarouselCardButton
            key={`btn-${i}`}
            btn={btn}
            btnIndex={i}
            onChange={handleButtonChange}
            onRemove={handleRemoveButton}
          />
        ))}

        {/* ============= Remove entire card ============= */}
        <Button
          variant="outlined"
          color="error"
          sx={{ mt: 2 }}
          onClick={onRemoveCard}
        >
          Delete
        </Button>
      </Box>
    );
  }
);

/** Memoized Normal Buttons Section */
const NormalButtonsSection = memo(() => {
  const dispatch = useDispatch();
  const { buttons } = useSelector((state) => state.templateCreation);
  const [quickRepliesErrors, setQuickRepliesErrors] = useState({});
  const [phoneButtonErrors, setPhoneButtonErrors] = useState({
    label: "",
    phoneNumber: "",
  });
  const [linkButtonsErrors, setLinkButtonsErrors] = useState([]);

  // Get total button count
  const getTotalButtonsCount = useCallback(() => {
    let total = 0;
    total += buttons.quickReplies.length;
    total += buttons.phoneButtons.length;
    total += buttons.linkButtons.length;
    return total;
  }, [buttons]);

  // Quick Reply Handlers
  const handleAddQuickReply = () => {
    if (getTotalButtonsCount() >= MAX_TOTAL_BUTTONS) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Max ${MAX_TOTAL_BUTTONS} buttons reached.`,
        })
      );
      return;
    }
    if (buttons.quickReplies.length >= MAX_QUICK_REPLIES) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Max ${MAX_QUICK_REPLIES} quick replies allowed.`,
        })
      );
      return;
    }
    dispatch(addQuickReply());

    // Set fieldsFlag to false since a new empty button was added
    dispatch(
      updateFieldsFlagAndMsg({
        fieldsFlag: false,
        fieldsErrorMsg: "Quick reply label cannot be empty",
      })
    );
  };

  const handleQuickReplyLabelChange = (index, label) => {
    const trimmed = label.trim();
    const localError = trimmed ? "" : "Field cannot be empty";
    setQuickRepliesErrors((prev) => ({ ...prev, [index]: localError }));

    // Set fieldsFlag to false when the field is empty (has an error)
    if (localError) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: localError,
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({ fieldsFlag: true, fieldsErrorMsg: "" })
      );
    }

    dispatch(updateQuickReply({ index, label }));
  };

  const handleRemoveQuickReply = (index) => {
    dispatch(removeQuickReply(index));
    setQuickRepliesErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  // Phone Button Handlers
  const handleAddPhoneButton = () => {
    if (getTotalButtonsCount() >= MAX_TOTAL_BUTTONS) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Max ${MAX_TOTAL_BUTTONS} buttons reached.`,
        })
      );
      return;
    }
    if (buttons.phoneButtons.length >= MAX_PHONE_BUTTONS) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Only ${MAX_PHONE_BUTTONS} phone button allowed.`,
        })
      );
      return;
    }
    dispatch(addPhoneButton());

    // Set fieldsFlag to false since a new empty button was added
    dispatch(
      updateFieldsFlagAndMsg({
        fieldsFlag: false,
        fieldsErrorMsg: "Phone button fields cannot be empty",
      })
    );
  };

  const handleRemovePhoneButton = () => {
    dispatch(removePhoneButton());
    setPhoneButtonErrors({ label: "", phoneNumber: "" });
    setPhoneInputValue("");
  };

  // Phone input value, formatted by mui-tel-input (same library used on the onboarding page)
  const [phoneInputValue, setPhoneInputValue] = useState("");

  const handlePhoneFieldChange = (field, value) => {
    let updatedErr = "";
    const trimmed = value.trim();

    // Validate label
    if (field === "label") {
      if (!trimmed) {
        updatedErr = "Phone button label cannot be empty";
      }
      setPhoneButtonErrors((prev) => ({ ...prev, label: updatedErr }));
    }

    // Set fieldsFlag to false when there's an error
    if (updatedErr) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: updatedErr,
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({ fieldsFlag: true, fieldsErrorMsg: "" })
      );
    }

    const phoneBtn = buttons.phoneButtons[0];
    if (phoneBtn) {
      dispatch(
        updatePhoneButton({
          label: field === "label" ? value : phoneBtn.label,
          countryCode: phoneBtn.countryCode,
          phoneNumber: phoneBtn.phoneNumber,
        })
      );
    }
  };

  // Phone number + country code, via mui-tel-input (same library used on the onboarding page)
  const handlePhoneNumberChange = (value, info) => {
    setPhoneInputValue(value);

    const digitsOnly = (info.nationalNumber || "").replace(/\D/g, "");
    let updatedErr = "";
    if (digitsOnly.length === 0) {
      updatedErr = "Phone number is required";
    } else if (digitsOnly.length < 7) {
      updatedErr = "Phone number must be at least 7 digits";
    } else if (digitsOnly.length > 15) {
      updatedErr = "Phone number cannot exceed 15 digits";
    }
    setPhoneButtonErrors((prev) => ({ ...prev, phoneNumber: updatedErr }));

    dispatch(
      updateFieldsFlagAndMsg(
        updatedErr
          ? { fieldsFlag: false, fieldsErrorMsg: updatedErr }
          : { fieldsFlag: true, fieldsErrorMsg: "" }
      )
    );

    const phoneBtn = buttons.phoneButtons[0];
    if (phoneBtn) {
      dispatch(
        updatePhoneButton({
          label: phoneBtn.label,
          countryCode: info.countryCallingCode ? `+${info.countryCallingCode}` : "",
          phoneNumber: digitsOnly,
        })
      );
    }
  };

  // Link Button Handlers
  const handleAddLinkButton = () => {
    if (getTotalButtonsCount() >= MAX_TOTAL_BUTTONS) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Max ${MAX_TOTAL_BUTTONS} buttons reached.`,
        })
      );
      return;
    }
    if (buttons.linkButtons.length >= MAX_LINK_BUTTONS) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: `Only ${MAX_LINK_BUTTONS} link buttons allowed.`,
        })
      );
      return;
    }
    setLinkButtonsErrors((prev) => [...prev, { label: "", link: "" }]);
    dispatch(
      addLinkButton({
        label: "",
        link: "https://",
        isLinkDynamic: false,
        dynamicVariableLabel: "",
      })
    );

    // Set fieldsFlag to false since a new empty button was added
    dispatch(
      updateFieldsFlagAndMsg({
        fieldsFlag: false,
        fieldsErrorMsg: "Link button fields cannot be empty",
      })
    );
  };

  const handleRemoveLinkButton = (index) => {
    dispatch(removeLinkButton(index));
    setLinkButtonsErrors((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleLinkFieldChange = (index, field, value) => {
    const linkBtn = buttons.linkButtons[index];
    if (!linkBtn) return;

    let localError = "";

    // Validate fields
    if (field === "label" && !value.trim()) {
      localError = "Button label cannot be empty";
    } else if (field === "link" && (!value || value === "https://")) {
      localError = "URL cannot be empty";
    } else if (
      field === "dynamicVariableLabel" &&
      isDynamicUrl(linkBtn.link) &&
      !value.trim()
    ) {
      localError = "Dynamic variable label is required";
    }

    // Update local errors
    setLinkButtonsErrors((prev) => {
      const newArr = [...prev];
      newArr[index] = newArr[index] || { label: "", link: "" };
      if (field === "label") newArr[index].label = localError;
      else if (field === "link") newArr[index].link = localError;
      return newArr;
    });

    // Update global error state - set fieldsFlag to false when there's an error
    if (localError) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: localError,
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({ fieldsFlag: true, fieldsErrorMsg: "" })
      );
    }

    // If changing isLinkDynamic, set it explicitly to true or false
    if (field === "isLinkDynamic") {
      value = !!value; // Ensure it's a boolean
    }

    // Update Redux with the new field value
    dispatch(
      updateLinkButton({
        index,
        [field]: value,
      })
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={handleAddQuickReply}>
          Add Quick Reply
        </Button>
        <Button variant="outlined" onClick={handleAddPhoneButton}>
          Add Phone Button
        </Button>
        <Button variant="outlined" onClick={handleAddLinkButton}>
          Add Link Button
        </Button>
      </Box>

      {/* Quick Replies */}
      {buttons.quickReplies.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            Quick Reply Buttons
          </Typography>
          {buttons.quickReplies.map((reply, index) => (
            <QuickReplyItem
              key={`qr-${index}`}
              reply={reply}
              index={index}
              onChange={handleQuickReplyLabelChange}
              onRemove={handleRemoveQuickReply}
              error={quickRepliesErrors[index] || ""}
            />
          ))}
        </Box>
      )}

      {/* Phone Buttons */}
      {buttons.phoneButtons.length > 0 && buttons.phoneButtons[0] && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Phone Button
            </Typography>
            <IconButton
              onClick={handleRemovePhoneButton}
              sx={{ fontSize: "1.3rem" }}
            >
              <DeleteIcon fontSize="large" />
            </IconButton>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              Label
            </Typography>
            <TextField
              fullWidth
              placeholder="Call us now"
              value={buttons.phoneButtons[0].label}
              error={!!phoneButtonErrors.label}
              helperText={phoneButtonErrors.label || ""}
              onChange={(e) => handlePhoneFieldChange("label", e.target.value)}
            />
          </Box>

          {/* Phone number, with country code flag picker (same mui-tel-input library as onboarding) */}
          <MuiTelInput
            fullWidth
            defaultCountry={DEFAULT_PHONE_COUNTRY}
            value={phoneInputValue}
            onChange={handlePhoneNumberChange}
            label="Phone Number"
            variant="outlined"
            margin="normal"
            required
            error={!!phoneButtonErrors.phoneNumber}
            helperText={phoneButtonErrors.phoneNumber || "Pick your country flag, then enter your number"}
          />
        </Box>
      )}

      {/* Link Buttons */}
      {buttons.linkButtons.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            Link Buttons
          </Typography>
          {buttons.linkButtons.map((linkBtn, index) => (
            <LinkButtonItem
              key={`link-${index}`}
              linkBtn={linkBtn}
              index={index}
              onChange={handleLinkFieldChange}
              onRemove={handleRemoveLinkButton}
              errors={linkButtonsErrors[index] || { label: "", link: "" }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

function TemplateButtonOptions() {
  const dispatch = useDispatch();
  const { selectedCategory, fieldsErrorMsg, buttonsMode } = useSelector(
    (state) => state.templateCreation
  );

  // Which radio is selected: "none" | "buttons" | "carousel" — persisted in
  // Redux (not local state) so it survives the step remount that happens on
  // Back/Next navigation instead of resetting to "none" and looking empty.
  const selectedOption = buttonsMode;
  const [error, setError] = useState("");

  // If the global slice sets an error, reflect it locally
  useEffect(() => {
    if (fieldsErrorMsg) setError(fieldsErrorMsg);
    else setError("");
  }, [fieldsErrorMsg]);

  // Whenever `error` changes, push to global slice or clear
  useEffect(() => {
    if (error) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: error,
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: true,
          fieldsErrorMsg: "",
        })
      );
    }
  }, [error, dispatch]);

  // ============= 2) Decide available radio options =============
  let availableOptions = ["none", "buttons"];
  if (selectedCategory === "Marketing") {
    availableOptions = ["none", "buttons", "carousel"];
  }

  // ============= 3) Radio change => clear old data if switching =============
  const handleOptionChange = (e) => {
    const newOption = e.target.value;
    if (newOption === selectedOption) return;
    dispatch(setButtonsMode(newOption));
    // Clear old buttons/carousel data only on an actual mode switch
    dispatch(clearAllButtons());
    // Carousel templates can't carry a top-level header or footer (Meta
    // only allows BODY + CAROUSEL at the top level) — clear any previously
    // entered ones.
    if (newOption === "carousel") {
      dispatch(updateFormData({ key: "header", value: "none" }));
      dispatch(updateFormData({ key: "headerData", value: "" }));
      dispatch(updateFormData({ key: "footer", value: "" }));
    }
  };

  // ============= MAIN RENDER =============
  return (
    <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6">Buttons</Typography>

      <RadioGroup
        row
        value={selectedOption}
        onChange={handleOptionChange}
        sx={{ gap: 2 }}
      >
        {availableOptions.map((opt) => (
          <FormControlLabel
            key={opt}
            value={opt}
            control={<MuiRadio />}
            label={
              opt === "none"
                ? "None"
                : opt === "buttons"
                ? "Buttons (Quick Reply + CTA)"
                : "Carousel"
            }
          />
        ))}
      </RadioGroup>

      {/* If there's local or global error */}
      {/* {error && <Alert severity="error">{error}</Alert>} */}

      {/* Normal Buttons */}
      {selectedOption === "buttons" && <NormalButtonsSection />}

      {/* Carousel (Marketing Only) */}
      {selectedCategory === "Marketing" && selectedOption === "carousel" && (
        <MarketingCarouselSection />
      )}
    </Box>
  );
}

export default TemplateButtonOptions;
